/**
 * The link test: a click-through of the project's saved questionnaire on a server session. Every click is
 * one call on the session, which holds the answers, the routing and the version tested. Questions, options,
 * rows and columns are named by their labels; a cell id is the instance path (the datamap label).
 */

/** single: an option id; multi: option ids; number or real: a number; text: a string; null: left blank */
export type SurveyAnswerValue = string | string[] | number | null
/** Answers by cell id */
export type SurveyAnswers = Record<string, SurveyAnswerValue>

export interface SurveyOption {
  /** The option label */
  id: string
  text: string
  exception: boolean
  exclusive: boolean
  /** Asks to specify: the cell's `open` names the text's cell */
  open: boolean
  terminates: boolean
}

export interface SurveyCell {
  /** The instance path */
  id: string
  /** Grid row label, "" outside a grid */
  row: string
  /** GRID FILL IN column label, "" otherwise */
  column: string
  /** The option ids shown in this cell after its filter, in display order */
  shown: string[]
  /** The open-text cell id of each option that asks to specify, by option id */
  open: Record<string, string>
}

/** Everything the runtime checks; a client uses it only for convenience checks. */
export interface SurveyRules {
  required: boolean
  atleast: number
  atmost: number
  min: number | null
  max: number | null
  whole: boolean
  unique: boolean
}

/** What is shown or checked differs from the live survey, on this card only */
export interface SurveyNote {
  code: string
  message: string
}

export interface SurveyCard {
  /** The question label; a TEXT element's label for an info card */
  question: string
  elem: "question" | "hidden" | "data"
  /** "info" is a TEXT element: an instruction with no cells */
  kind: "single" | "multi" | "number" | "real" | "text" | "info"
  layout: "plain" | "rows" | "grid"
  section: string
  text: string
  /** Each enclosing FOR EACH, outer first */
  loop: { driver: string; iteration: string; text: string }[]
  options: SurveyOption[]
  rows: { id: string; text: string }[]
  columns: { id: string; text: string }[]
  cells: SurveyCell[]
  rules: SurveyRules
  notes: SurveyNote[]
}

export interface SurveyCellError {
  /** The cell id, an open text's included */
  cell: string
  rule: string
  message: string
}

export interface SurveyRoutingStep {
  /** "IF", "TERMINATE", "SKIP TO" or "FOR EACH" */
  kind: string
  /**
   * The premise as written in the questionnaire; a SKIP TO's is "premise -> target", a FOR EACH's
   * "driver = iteration"
   */
  text: string
  /** Whether the condition held: an IF asked, a TERMINATE or SKIP TO taken, a FOR EACH iteration run */
  result?: boolean
}

/** The session's answer to every call: where the test is now. */
export interface SurveyResponse {
  session: string
  version: string
  variant: string
  language: string
  seed: number
  /** Goes up by one each time a screen is accepted */
  step: number
  status: "question" | "end" | "terminated"
  /** The page (status "question"): one card per question, answered together */
  screen: { page: number; revision: number; cards: SurveyCard[] }
  /** A refused submit or a re-shown page: the values given */
  values: SurveyAnswers
  /** A refused submit: the problems, by cell */
  errors: SurveyCellError[]
  /** Values given for cells that no longer applied */
  ignored: string[]
  terminated: { by: "option" | "condition"; at?: string; text: string } | null
  /** The IF and FOR EACH conditions the walk took to reach the page's first card */
  why: SurveyRoutingStep[]
  /** Every routing decision so far */
  trace: SurveyRoutingStep[]
  diagnostics: { code: string; severity: string; where: string; message: string }[]
  /** A fill's end: every answer */
  answers?: SurveyAnswers
  /** A start: whether the version was prepared for it */
  prepared?: boolean
}

export interface SurveyStartRequest {
  /** Empty or absent: the questionnaire's first variant */
  variant?: string
  /**
   * Empty or absent: the variant's design language, the first language of its #LANGUAGE list, whose texts
   * are the questionnaire's own. Any other language is named, and the project's translations are merged for it.
   */
  language?: string
  seed?: number
  acceptFallbacks?: boolean
}

export interface SurveySessionRequest {
  session: string
}

export interface SurveySubmitRequest extends SurveySessionRequest {
  step: number
  answers: SurveyAnswers
}

export interface SurveyFillRequest extends SurveySessionRequest {
  step: number
  /** The screen's answers, submitted before the rest is filled */
  answers?: SurveyAnswers
}

export interface SurveyLanguageRequest extends SurveySessionRequest {
  /** "" is the design language, as for a start */
  language: string
}

/** A failed call, with the route's error code ("session_not_found", "stale_step", ...) when it gave one. */
export class SurveyTestError extends Error {
  constructor(
    message: string,
    readonly code?: string
  ) {
    super(message)
  }
}
